/**
 * One SERVICE on the job page — the unit the shop agrees, assigns, times
 * and finishes since tasks per service were retired (2026-09-03).
 *
 * Header: name (numbered "Service i/n" on a multi-service job), who holds
 * it, the approval badge, the live time, and the state's own control —
 * Pause while running, Resume while paused, Start while claimed and
 * not started, a colleague's name when it is theirs, "open" when nobody has
 * it (the claim is the job's, above the cards). Under it: the manager's
 * description and the parts on the service. Expanded, the workspace shows
 * manual time entry and the Finish action — notes live on the job as a
 * whole now (WoJobNotes), not per service (2026-09-22).
 *
 * The backend still keeps one task under the service; every action here
 * goes through `the service id`. Nothing is rendered as a step.
 * No prices anywhere — the tech side is time only.
 */
import { ArrowRight, CaretDown, CaretRight, Check, Pause, Phone, Play } from '@phosphor-icons/react'
import { ButtonSpinner } from '@vollgas/shared/ui'
import { cn, formatDuration } from '@vollgas/shared/utils'
import type { WoDetailService } from '@/apis/types'
import { useI18n } from '@/i18n/TranslationContext'
import { useCompleteTask } from '@/hooks/useWorkshop'
import { serviceView, serviceViewMinutes } from './serviceView'
import { WoServiceWorkspace } from './WoServiceWorkspace'

interface Props {
  readonly workOrderId: string
  readonly service: WoDetailService
  /** 2+ services on the job → number the header "SERVICE i/n". */
  readonly numbered: boolean
  readonly now: number
  readonly expanded: boolean
  readonly onToggle: () => void
  readonly onStart: (serviceId: string) => void
  readonly onPause: (serviceId: string) => void
  /** A start or pause for THIS service is in flight — one flag, not one per
   *  direction: the optimistic patch swaps the button the moment it is
   *  tapped, so a direction-specific flag dies with the button it was on. */
  readonly timerPending: boolean
  /** The tech already holds a stake elsewhere on this job — a loose service
   *  offers its own "Take service" instead of the plain "Open" line. */
  readonly stake: boolean
  readonly onTakeService: (serviceId: string) => void
  /** A per-service claim on THIS service is in flight. */
  readonly takingService: boolean
}

const CARD = 'overflow-hidden rounded-md border bg-glass-fill text-glass-text'

export function WoServiceCard({
  workOrderId, service, numbered, now, expanded, onToggle, onStart, onPause, timerPending,
  stake, onTakeService, takingService,
}: Props) {
  const { t } = useI18n()
  const view = serviceView(service)
  const minutes = formatDuration(serviceViewMinutes(view, now), { days: true })
  const label = numbered
    ? `${t.tasks.service} ${service.index}/${service.total} · ${service.name}`
    : service.name
  const serviceId = service.id
  const stop = (e: React.SyntheticEvent) => e.stopPropagation()
  const running = view.state === 'in_progress' && view.mine
  const paused = view.state === 'paused' && view.mine
  const claimed = view.state === 'claimed' && view.mine
  const done = view.state === 'done'
  const pending = view.state === 'awaiting_approval'
  const loose = view.state === 'open'
  // Offered only once the tech already holds a stake elsewhere on the job —
  // see `hasStake`.
  const takeable = loose && stake
  // A finished service, an unagreed one and one nobody holds are read-only.
  const canWrite = view.mine && !done
  // Only a body worth opening makes the header a toggle. The body used to
  // hold a read-only notes list for every state; since the per-service
  // notes went (2026-09-22) it is the workspace, which only the service's
  // own technician gets — so a done, loose or colleague's service is a
  // plain header, not a button that flips a caret onto nothing.
  const expandable = canWrite

  const owner = view.mechanicName === null
    ? null
    : (view.mine ? t.tasks.you : view.mechanicName)

  const border = running
    ? 'border-2 border-green'
    : paused || claimed
      ? 'border-2 border-dashed border-green'
      : 'border-glass-border'

  return (
    <div className={cn(CARD, border, (pending || done) && 'opacity-80')}>
      {/* Header — the state's own control lives here. */}
      <div
        {...(expandable
          ? {
              role: 'button' as const,
              tabIndex: 0,
              'aria-expanded': expanded,
              onClick: onToggle,
              onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter') onToggle() },
            }
          : {})}
        className={cn(
          'flex w-full items-center justify-between gap-3 px-4 py-3 text-left',
          expandable && 'cursor-pointer',
          running && 'bg-green text-white',
        )}
      >
        <span className="flex min-w-0 flex-col">
          <span className="flex min-w-0 items-center gap-1.5 truncate text-14 font-bold">
            {expandable
              ? (expanded ? <CaretDown size={13} weight="bold" aria-hidden="true" /> : <CaretRight size={13} weight="bold" aria-hidden="true" />)
              : <CaretRight size={13} weight="bold" className="opacity-40" aria-hidden="true" />}
            {done && '✓ '}{label}
          </span>
          <span className={cn('mt-0.5 flex flex-wrap items-center gap-1.5 pl-5 text-11-5', running ? 'text-white/80' : 'text-glass-text-muted')}>
            {owner && <span>{owner}</span>}
            {pending && (
              <span className="flex items-center gap-1">
                <Phone size={11} weight="fill" aria-hidden="true" /> {t.tasks.awaitingCustomer}
              </span>
            )}
            {paused && <span>{t.tasks.yoursLogged(minutes)}</span>}
            {done && <span>{t.workOrder.done} · {minutes}</span>}
            {/* Plain "Open" once the tech has a stake (the button says the
                rest); otherwise the whole-job claim above is what starts it. */}
            {loose && !stake && <span>{t.tasks.statusOpen} · {t.workOrder.claimToStart}</span>}
            {loose && stake && <span>{t.tasks.statusOpen}</span>}
            {!view.mine && (view.state === 'claimed') && <span>{t.tasks.claimedNotStarted}</span>}
            {!view.mine && (view.state === 'paused') && <span>{t.workOrder.timerPaused} · {minutes}</span>}
          </span>
        </span>

        {/* Right side: the control for this state. Wrapped so a tap on a
            button (or its portalled popover) does not also toggle the card. */}
        <span className="flex flex-shrink-0 items-center gap-2" onClick={stop} onKeyDown={stop}>
          {running && (
            <>
              <span className="rounded-full border border-white/40 px-2 py-0.5 text-10-5 font-bold uppercase tracking-mini">{t.tasks.you} · {t.tasks.running}</span>
              <span className="font-display text-15 font-extrabold tracking-mk-neg2">{minutes}</span>
            </>
          )}
          {view.state === 'in_progress' && !view.mine && (
            <span className="flex items-center gap-2 text-12-5 text-glass-text-muted">
              <span>{t.tasks.onIt}</span>
              <span className="rounded bg-glass-text px-1.5 py-0.5 text-10-5 font-bold text-white">{minutes}</span>
            </span>
          )}
          {paused && (
            <button
              type="button"
              onClick={() => onStart(serviceId)}
              disabled={timerPending}
              className="inline-flex items-center gap-1.5 rounded-full bg-green px-4 py-2 text-13 font-bold text-white transition hover:opacity-90 active:scale-[0.97] disabled:opacity-60"
            >
              {timerPending ? <ButtonSpinner /> : <Play size={13} weight="fill" aria-hidden="true" />} {t.tasks.resume}
            </button>
          )}
          {claimed && (
            <button
              type="button"
              onClick={() => onStart(serviceId)}
              disabled={timerPending}
              className="inline-flex items-center gap-1.5 rounded-full bg-green px-4 py-2 text-13 font-bold text-white transition hover:opacity-90 active:scale-[0.97] disabled:opacity-60"
            >
              {timerPending ? <ButtonSpinner /> : <Play size={13} weight="fill" aria-hidden="true" />} {t.tasks.start}
            </button>
          )}
          {pending && (
            <span className="rounded border border-dashed border-glass-border px-2 py-0.5 text-10-5 font-bold uppercase tracking-mk-up12 text-glass-text-muted">
              {t.tasks.approval}
            </span>
          )}
          {takeable && (
            <button
              type="button"
              onClick={() => onTakeService(serviceId)}
              disabled={takingService}
              className="inline-flex items-center gap-1.5 rounded-full border border-glass-border bg-transparent px-4 py-2 text-13 font-bold text-glass-text transition hover:bg-glass-text hover:text-white active:scale-[0.97] disabled:opacity-60"
            >
              {takingService && <ButtonSpinner />}
              {t.tasks.takeService}
              {!takingService && <ArrowRight size={13} weight="bold" aria-hidden="true" />}
            </button>
          )}
        </span>
      </div>

      {/* What the manager wrote, and what is on the car for it. */}
      {(service.description || service.parts.length > 0) && (
        <div className={cn('flex flex-col gap-1.5 px-4 pb-3', !running && 'pt-0')}>
          {service.description
            ? <p className="whitespace-pre-line text-13 text-glass-text">{service.description}</p>
            : null}
          {service.parts.length > 0 && (
            <ul className="flex flex-col gap-0.5">
              {service.parts.map((p) => (
                <li key={p.id} className="min-w-0 truncate text-12-5">
                  <span className="font-semibold text-glass-text">{p.name}</span>
                  <span className="text-glass-text-muted"> {t.workOrder.sku} {p.sku} · {p.qty}×</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Nothing left to show once a service is read-only: notes moved to
          the job page, so a colleague's or finished service has no
          workspace content — expanding it just reveals its description
          and parts above, already visible. */}
      {expandable && expanded && canWrite && (
        <div className="border-t border-glass-divider">
          <WoServiceWorkspace
            serviceId={serviceId}
            canWrite={canWrite}
            actions={running ? <RunningActions workOrderId={workOrderId} serviceId={serviceId} onPause={onPause} pausing={timerPending} /> : null}
          />
        </div>
      )}
    </div>
  )
}

/** The running service's own footer actions — Pause (orange) + Finish (green). */
function RunningActions({
  workOrderId, serviceId, onPause, pausing,
}: { readonly workOrderId: string; readonly serviceId: string; readonly onPause: (serviceId: string) => void; readonly pausing: boolean }) {
  const { t } = useI18n()
  const complete = useCompleteTask()
  return (
    <>
      <button
        type="button"
        onClick={() => onPause(serviceId)}
        disabled={pausing}
        className="inline-flex items-center gap-1.5 rounded-full bg-orange px-4 py-2 text-13 font-bold text-white transition hover:opacity-90 active:scale-[0.97] disabled:opacity-60"
      >
        {pausing ? <ButtonSpinner /> : <Pause size={13} weight="fill" aria-hidden="true" />} {t.tasks.pause}
      </button>
      <button
        type="button"
        onClick={() => complete.mutate({ workOrderId, serviceId })}
        disabled={complete.isPending}
        className="inline-flex items-center gap-1.5 rounded-full bg-green px-4 py-2 text-13 font-bold text-white transition hover:opacity-90 active:scale-[0.97] disabled:opacity-60"
      >
        {complete.isPending ? <ButtonSpinner /> : <Check size={14} weight="bold" aria-hidden="true" />} {t.workOrder.finishTask}
      </button>
    </>
  )
}
